// Composable for managing header/footer preview
// Handles debounced preview updates and viewport switching
import { ref, computed } from 'vue'

export function usePreview(apiEndpoints) {
  const headerHtml = ref('')
  const footerHtml = ref('')
  const isLoading = ref(false)
  const error = ref(null)
  const viewport = ref('desktop')

  // Debounce timer
  let debounceTimer = null
  const DEBOUNCE_MS = 500

  // Viewport widths
  const viewportWidth = computed(() => {
    switch (viewport.value) {
      case 'mobile':
        return '375px'
      case 'tablet':
        return '768px'
      case 'desktop':
      default:
        return '100%'
    }
  })

  // Set viewport mode
  function setViewport(mode) {
    viewport.value = mode
  }

  // Fetch header preview from server
  async function fetchHeaderPreview(content, settings, template) {
    try {
      const params = new URLSearchParams({
        content: JSON.stringify(content),
        settings: JSON.stringify(settings),
        header_block_type: template
      })

      const response = await fetch(`${apiEndpoints.preview_header}?${params}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })

      const data = await response.json()

      if (data.success) {
        headerHtml.value = data.html
        return { success: true }
      } else {
        throw new Error(data.error || 'Preview failed')
      }
    } catch (err) {
      console.error('Header preview error:', err)
      error.value = err.message
      return { success: false, error: err.message }
    }
  }

  // Fetch footer preview from server
  async function fetchFooterPreview(content, settings, template) {
    try {
      const params = new URLSearchParams({
        content: JSON.stringify(content),
        settings: JSON.stringify(settings),
        footer_block_type: template
      })

      const response = await fetch(`${apiEndpoints.preview_footer}?${params}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })

      const data = await response.json()

      if (data.success) {
        footerHtml.value = data.html
        return { success: true }
      } else {
        throw new Error(data.error || 'Preview failed')
      }
    } catch (err) {
      console.error('Footer preview error:', err)
      error.value = err.message
      return { success: false, error: err.message }
    }
  }

  // Fetch both previews
  async function fetchPreviews(headerData, footerData) {
    isLoading.value = true
    error.value = null

    try {
      await Promise.all([
        fetchHeaderPreview(headerData.content, headerData.settings, headerData.template),
        fetchFooterPreview(footerData.content, footerData.settings, footerData.template)
      ])
      return { success: true }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  // Debounced preview update
  function debouncedFetchPreviews(headerData, footerData) {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = setTimeout(() => {
      fetchPreviews(headerData, footerData)
    }, DEBOUNCE_MS)
  }

  // Cancel pending preview fetch
  function cancelPendingFetch() {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  // Generate combined preview HTML for iframe
  function generatePreviewDocument() {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body { margin: 0; padding: 0; }
            /* Add spacing between header and footer for preview */
            .preview-spacer { height: 200px; background: #f9fafb; display: flex; align-items: center; justify-content: center; }
            .preview-spacer-text { color: #9ca3af; font-size: 14px; }
          </style>
        </head>
        <body>
          ${headerHtml.value}
          <div class="preview-spacer">
            <span class="preview-spacer-text">Page content area</span>
          </div>
          ${footerHtml.value}
        </body>
      </html>
    `
  }

  return {
    headerHtml,
    footerHtml,
    isLoading,
    error,
    viewport,
    viewportWidth,
    setViewport,
    fetchHeaderPreview,
    fetchFooterPreview,
    fetchPreviews,
    debouncedFetchPreviews,
    cancelPendingFetch,
    generatePreviewDocument
  }
}

export default usePreview
